<template>
  <div class="examination_assignments_table">
    <div class="d-flex justify-end">
      <v-btn
        v-if="isMultiUser"
        color="primary"
        class="mt-4 mr-2"
        @click="openMultiUserDialog"
      >
        <v-icon left>mdi-swap-horizontal</v-icon>
        {{ $t("multiUser.button") }}
      </v-btn>
      <v-btn
        v-if="canCreateHere"
        color="secondary"
        outlined
        class="mt-4 mr-4"
        @click="newDocument()"
      >
        {{ $t("examination.new") }}
        <v-icon right>add</v-icon>
      </v-btn>
    </div>
    <v-data-table
      v-bind:headers="headers"
      v-bind:items="allExaminations"
      :options.sync="tableDataGetter.pagination"
      :footer-props="{
        itemsPerPageOptions: tableDataGetter.pagination.rowsPerPageItems,
        itemsPerPageText: $t('general.rowsPerPage'),
      }"
      @click:row="goToExamination($event._id, allExaminations)"
    >
      <template v-slot:item.examinationOptional="{ item }">
        <div v-if="item.optional">
          {{ $t("examination.tableHeader.proposalShort") }}
        </div>
        <div v-else>
          {{ $t("examination.tableHeader.obligatoryShort") }}
        </div>
      </template>
      <template v-slot:item.examinationAssigned="{ item }">
        <v-chip
          color="primary"
          outlined
          class="px-4"
          ripple
          @click:row="goToExamination($event._id, allExaminations)"
        >
          {{ item.assignedCount ?? 0 }}
        </v-chip>
      </template>
      <template v-slot:item.examinationOpen="{ item }">
        <v-chip
          color="red"
          outlined
          class="px-4"
          ripple
          @click:row="goToExamination($event._id, allExaminations)"
        >
          {{ item.openAssignmentCount ?? 0 }}
        </v-chip>
      </template>
      <template v-slot:item.examinationAction="{ item }">
        <examinationAction :item="item" @reloadData="reloadData()" />
      </template>
    </v-data-table>

    <examination-assignments-assigned-dialog
      ref="assigned"
      :examination-id="dialogAssigned.examinationId"
      @close="reloadData()"
    />

    <!-- at the bottom, before closing </div> -->
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="examination"
      :availableItems="allExaminations"
      itemText="name"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.examinations"
    />
  </div>
</template>

<script>
import ExaminationAssignmentsAssignedDialog from "@/views/examination/examinationAssignmentsAssignedDialog.vue";
import { examinationAction } from "@/components/tableSlotComponents";
import { mapGetters } from "vuex";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";

export default {
  components: {
    ExaminationAssignmentsAssignedDialog,
    examinationAction,
    multiUserDialog,
  },
  computed: {
    ...mapGetters([
      "allExaminations",
      "allExaminationsPending",
      "tableDataGetter",
    ]),
    headers() {
      return [
        {
          text: this.$t("examination.tableHeader.examination"),
          value: "name",
        },
        {
          text: this.$t("examination.tableHeader.optional"),
          value: "examinationOptional",
        },
        {
          text: this.$t("examination.tableHeader.assigned"),
          value: "examinationAssigned",
        },
        {
          text: this.$t("examination.tableHeader.open"),
          value: "examinationOpen",
        },
        {
          text: "",
          value: "examinationAction",
          align: "right",
          sortable: false,
        },
      ];
    },
  },
  data: () => ({
    dialogAssigned: {
      examinationId: undefined,
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
  }),
  async created() {
    this.$store.dispatch("getAllExaminations");

    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";
  },
  methods: {
    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            this.$t("multiUser.result.success", {
              number: result.totalSuccess,
            }),
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalConflict }),
          );
        }
        if (result.totalError > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalError }),
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadData();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },

    goToExamination(examinationId, items) {
      const currentExamination = items.find(
        (item) => item._id === examinationId,
      );

      this.$router.push({
        name: "examination-details",
        params: {
          examinationId: examinationId,
          examinationName: currentExamination.name,
        },
      });
    },
    reloadData() {
      this.$store.dispatch("getAllExaminations");
    },
    _open(examinationId, tabNumber) {
      this.dialogAssigned.examinationId = examinationId;
      this.$nextTick(() => this.$refs.assigned.openDialog(tabNumber));
    },
    openAssigned(examinationId) {
      this._open(examinationId, 0);
    },
    openOpenAssignments(examinationId) {
      this._open(examinationId, 1);
    },
    newDocument() {
      this.$router.push({
        name: "examinationForm",
        params: { id: "1", mode: "new", data: 1 },
      });
    },
  },
};
</script>

<style>
div.examination_assignments_table th[role="columnheader"].sortable {
  white-space: nowrap;
}
div.examination_assignments_table tr {
  cursor: pointer;
}
</style>
